import React, { useState, useEffect, useMemo } from "react";
import config from "../../Config";
import axios from "axios";
import { Link } from "react-router-dom";
import Monthly_navbar from "./Monthlytasks_navbar";
import arrears from "../../assets/images/dashboard_icon/arrears.svg";
import no_fee from "../../assets/images/dashboard_icon/no_fee.svg";
import { Bar } from "react-chartjs-2";
import moment from "moment";
import Spinner from "react-bootstrap/Spinner";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import MonthsDropDown from "../Shared/MonthsDropDown";
import YearsDropDown from "../Shared/YearsDropDown";

/** Normalize API table/list payload for class-wise admissions */
function normalizeAdmissionList(raw) {
  if (!raw) return [];
  if (!Array.isArray(raw)) return [];
  return raw.map((r) => ({
    Class_Name: r.Class_Name != null ? String(r.Class_Name) : "—",
    NoOfStudents:
      parseInt(String(r.NoOfStudents != null ? r.NoOfStudents : 0), 10) || 0,
  }));
}

const MonthlyTasksDashboard = () => {
  const [AccessKey] = useState(localStorage.getItem("AccessKey"));
  const [UserID] = useState(localStorage.getItem("ID"));
  const [MemberType] = useState(
    localStorage.getItem("MemberType") || "Admin"
  );
  const [loading, setLoading] = useState(true);

  const [Month, setMonth] = useState(moment().format("M"));
  const [Year, setYear] = useState(moment().format("YYYY"));

  const [ListofRecords, setListofRecords] = useState([]);
  const [PostedFee, setPostedFee] = useState([]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: { display: false },
        legend: { display: false },
        tooltip: {
          mode: "index",
          intersect: false,
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          titleColor: "white",
          bodyColor: "white",
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          stacked: false,
          ticks: { precision: 0 },
          grid: { drawTicks: false },
        },
        x: {
          grid: { display: false },
        },
      },
    }),
    []
  );

  useEffect(() => {
    document.title = "Monthly Tasks — Fee Posting";
  }, []);

  useEffect(() => {
    if (!AccessKey || !UserID) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const m = parseInt(Month, 10);
    const y = parseInt(Year, 10);

    const basePayload = {
      AccessKey,
      UserID,
      MemberType,
      SelectedMonth: m,
      FYear: y,
    };

    const admissionsReq = axios({
      method: "post",
      url: config.base_url + "MonthlyTask/GetMonthlyAdmissionDetails",
      headers: { "Content-Type": "application/json" },
      data: basePayload,
    });

    const monthsReq = axios({
      method: "post",
      url: config.base_url + "MonthlyTask/GetStartedMonths",
      headers: { "Content-Type": "application/json" },
      data: {
        ...basePayload,
        pageNo: 1,
        pageSize: 500,
        SchoolID: 1,
      },
    });

    Promise.all([admissionsReq, monthsReq])
      .then(([admissionsRes, monthsRes]) => {
        if (admissionsRes.data && admissionsRes.data.status_code === 1) {
          setListofRecords(
            normalizeAdmissionList(admissionsRes.data.ListofRecords)
          );
        } else {
          setListofRecords([]);
        }

        if (monthsRes.data && monthsRes.data.status_code === 1) {
          setPostedFee(monthsRes.data.ListofRecords || []);
        } else {
          setPostedFee([]);
        }
      })
      .catch(function () {
        toast.error("Network Error..", {
          position: toast.POSITION.TOP_RIGHT,
        });
        setListofRecords([]);
        setPostedFee([]);
      })
      .finally(() => setLoading(false));
  }, [Month, Year, AccessKey, UserID, MemberType]);

  const totalNewAdmissions = useMemo(
    () => ListofRecords.reduce((s, r) => s + (r.NoOfStudents || 0), 0),
    [ListofRecords]
  );

  const classesWithAdmissions = ListofRecords.length;

  const isFeePostedForSelectedPeriod = useMemo(() => {
    const m = parseInt(Month, 10);
    const y = parseInt(Year, 10);
    return PostedFee.some(
      (p) =>
        parseInt(String(p.Month_Number), 10) === m &&
        parseInt(String(p.Current_Year), 10) === y
    );
  }, [PostedFee, Month, Year]);

  const lastPostedMonth = useMemo(() => {
    if (!PostedFee || PostedFee.length === 0) return null;
    return [...PostedFee].sort((a, b) => b.Month_ID - a.Month_ID)[0];
  }, [PostedFee]);

  const periodLabel = moment(`${Year}-${Month}-1`, "YYYY-M-D").format(
    "MMMM YYYY"
  );

  const barChartData = useMemo(() => {
    const labels = ListofRecords.map((r) => r.Class_Name || "—");
    const values = ListofRecords.map((r) => r.NoOfStudents || 0);
    return {
      labels,
      datasets: [
        {
          label: "New admissions",
          data: values,
          backgroundColor: "#003970",
          borderWidth: 1,
          barPercentage: 0.5,
          borderRadius: 6,
          borderSkipped: false,
        },
      ],
    };
  }, [ListofRecords]);

  const renderMonthlyTask = () => {
    if (ListofRecords != null && ListofRecords.length > 0) {
      return ListofRecords.map((item, index) => {
        return (
          <li key={index}>
            <div className="left">
              <h5>{item.Class_Name}</h5>
            </div>
            <div className="right">
              <h4>{item.NoOfStudents}</h4>
            </div>
          </li>
        );
      });
    }
    return (
      <li className="text-muted py-2 px-2" style={{ listStyle: "none" }}>
        No class-wise admission data for {periodLabel}.
      </li>
    );
  };

  const renderPostedFee = () => {
    if (PostedFee != null && PostedFee.length > 0) {
      const m = parseInt(Month, 10);
      const y = parseInt(Year, 10);
      const filtered = PostedFee.filter(
        (item) =>
          parseInt(String(item.Month_Number), 10) === m &&
          parseInt(String(item.Current_Year), 10) === y
      );
      const rows = filtered.length > 0 ? filtered : PostedFee;

      return rows.map((item, index) => {
        return (
          <tr key={index}>
            <td>{item.Month_ID}</td>
            <td>{item.Month_Number}</td>
            <td>{item.Current_Year}</td>
            <td className="text-center">
              {item.Started_On
                ? moment(item.Started_On).format("DD/MM/YYYY")
                : "—"}
            </td>
          </tr>
        );
      });
    }
    return (
      <tr>
        <td colSpan="4">
          <h4
            className="mt-3 mb-0"
            style={{ fontSize: "15px", color: "rgba(0,0,0,0.6)" }}
          >
            No posted fee months found.
          </h4>
        </td>
      </tr>
    );
  };

  return (
    <>
      <Monthly_navbar />
      <ToastContainer />
      <div className="page-head">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-6">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/Dashboard">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Monthly Tasks — Fee Posting
                  </li>
                </ol>
              </nav>
              <div className="page-title">Monthly Tasks</div>
              <p className="text-muted small mb-0">
                Fee posting, vouchers, and admissions for the selected period.
              </p>
            </div>
            <div className="col-md-6">
              <div className="page-right-actions d-flex flex-wrap justify-content-md-end gap-2">
                <Link
                  to="/MonthlyTasks/StartNewMonth"
                  className="button button-white button-icon"
                >
                  Post Fee
                  <i className="ri-add-line"></i>
                </Link>
                <Link
                  to="/MonthlyTasks/ManageMonth"
                  className="button button-white button-icon"
                >
                  Manage Posted Fee
                  <i className="ri-list-check"></i>
                </Link>
                <Link
                  to="/MonthlyTasks/ManageVouchers"
                  className="button button-white button-icon"
                >
                  Manage Vouchers
                  <i className="ri-file-list-3-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="page-content">
        <div className="container">
          <div className="card mb-3">
            <div className="card-body">
              <div className="row align-items-end gy-3">
                <div className="col-md-3">
                  <label className="form-label mb-1">Month</label>
                  <select
                    className="form-select"
                    value={Month}
                    onChange={(e) => setMonth(e.target.value)}
                  >
                    <MonthsDropDown />
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label mb-1">Year</label>
                  <select
                    className="form-select"
                    value={Year}
                    onChange={(e) => setYear(e.target.value)}
                  >
                    <YearsDropDown />
                  </select>
                </div>
                <div className="col-md-6">
                  <div className="p-3 rounded border bg-light">
                    <div className="fw-semibold">{periodLabel}</div>
                    <div className="small mt-1">
                      Fee posted for this period:{" "}
                      <strong>
                        {loading
                          ? "…"
                          : isFeePostedForSelectedPeriod
                          ? "Yes"
                          : "No"}
                      </strong>
                    </div>
                    {lastPostedMonth && (
                      <div className="small text-muted mt-1">
                        Latest posted month in system: Month{" "}
                        {lastPostedMonth.Month_Number} /{" "}
                        {lastPostedMonth.Current_Year}
                        {lastPostedMonth.Started_On
                          ? ` — ${moment(lastPostedMonth.Started_On).format(
                              "DD/MM/YYYY"
                            )}`
                          : ""}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row gy-3 align-items-center">
            <div className="col-md-12">
              <div className="card m-card">
                <div className="card-body p-2">
                  <p className="text-center m-0">
                    <b>Posting overview</b>
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card m-card h-100">
                <div className="card-body text-center">
                  <img src={arrears} className="pb-3" alt="" />
                  <p className="m-2 small">New admissions ({periodLabel})</p>
                  <h4 className="m-2">
                    {loading ? <Spinner size="sm" /> : totalNewAdmissions}
                  </h4>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card m-card h-100">
                <div className="card-body text-center">
                  <img src={arrears} className="pb-3" alt="" />
                  <p className="m-2 small">Classes with new admissions</p>
                  <h4 className="m-2">
                    {loading ? <Spinner size="sm" /> : classesWithAdmissions}
                  </h4>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100">
                <div className="card-body text-center">
                  <img src={no_fee} className="pb-3" alt="" />
                  <p className="m-2 small">Fee posted for selected period</p>
                  <h4 className="m-2">
                    {loading ? (
                      <Spinner size="sm" />
                    ) : isFeePostedForSelectedPeriod ? (
                      "Yes"
                    ) : (
                      "No"
                    )}
                  </h4>
                </div>
              </div>
            </div>
            <div className="col-md-12">
              <div className="card">
                <div className="card-body">
                  <p className="small text-muted mb-2">
                    Posted fee periods (filtered to {periodLabel} when a match
                    exists; otherwise all periods).
                  </p>
                  <table className="table table-theme table-condensed pb-md-0 mb-0">
                    <thead style={{ backgroundColor: "#F8F8F8" }}>
                      <tr className=" mb-1">
                        <th colSpan="4" className="text-center">
                          MANAGE POSTED FEE
                        </th>
                      </tr>
                    </thead>
                    <thead style={{ backgroundColor: "#F8F8F8" }}>
                      <tr>
                        <th>Month ID</th>
                        <th>Month</th>
                        <th>Year</th>
                        <th className="text-center">Started On</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="4" className="text-center py-4">
                            <Spinner animation="border" role="status" />
                          </td>
                        </tr>
                      ) : (
                        renderPostedFee()
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          <div className="graph mt-5">
            <div className="row">
              <div className="col-md-8">
                <div className="earning-card dash-bg-card">
                  <div className="graph-bar">
                    <div className="dash-header">
                      <div className="heading">
                        Class-wise new admissions ({periodLabel})
                      </div>
                    </div>
                    <div style={{ minHeight: 320 }}>
                      {loading ? (
                        <div className="text-center py-5">
                          <Spinner animation="border" role="status" />
                        </div>
                      ) : ListofRecords.length === 0 ? (
                        <p
                          className="text-muted text-center py-5 mb-0"
                          style={{ fontSize: "15px" }}
                        >
                          No admission data for this month and year.
                        </p>
                      ) : (
                        <Bar data={barChartData} options={chartOptions} />
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card-body dash-bg-card">
                  <h4 className="card-title">Class-wise admissions</h4>
                  <div>
                    <ul className="birthday-list scrollbar">
                      {loading ? (
                        <div className="row">
                          <div className="col-md-12 text-center py-3">
                            <Spinner animation="border" role="status" />
                          </div>
                        </div>
                      ) : (
                        <>{renderMonthlyTask()}</>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MonthlyTasksDashboard;
